import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { MoodCard } from "./MoodCard";

const DURATION_MS = 3000;
const FPS = 30;

const ANIM_STYLES = [
    { key: "breath",   label: "Breath",     hint: "soft pulse + drift" },
    { key: "parallax", label: "Parallax",   hint: "depth in motion" },
    { key: "kenburns", label: "Ken-Burns",  hint: "slow zoom + pan" },
    { key: "inkbloom", label: "Ink-Bloom",  hint: "spreading vignette" },
    { key: "neon",     label: "Neon",       hint: "shimmer & glow" },
];

/** Auto-suggest the most fitting animation style for the card's pack + style combo. */
function suggestStyle(card) {
    if (!card) return "breath";
    const key = `${card.pack || "cinematic"}:${card.style}`;
    const map = {
        "cinematic:relatable": "breath",
        "cinematic:poetic":    "kenburns",
        "cinematic:bold":      "parallax",
        "vintage:relatable":   "kenburns",
        "vintage:poetic":      "inkbloom",
        "vintage:bold":        "parallax",
        "brutalist:relatable": "parallax",
        "brutalist:poetic":    "inkbloom",
        "brutalist:bold":      "neon",
        "minimal:relatable":   "breath",
        "minimal:poetic":      "inkbloom",
        "minimal:bold":        "kenburns",
    };
    return map[key] || "breath";
}

function drawFrame(style, ctx, img, W, H, t /* 0..1 */) {
    ctx.fillStyle = "#050507";
    ctx.fillRect(0, 0, W, H);
    ctx.save();

    if (style === "breath") {
        const breath = 0.5 + 0.5 * Math.sin(t * Math.PI * 2);
        const scale = 1 + 0.03 * breath;
        const dx = Math.sin(t * Math.PI * 2) * 6;
        const dy = Math.cos(t * Math.PI * 2) * 4;
        ctx.translate(W / 2 + dx, H / 2 + dy);
        ctx.scale(scale, scale);
        ctx.drawImage(img, -W / 2, -H / 2, W, H);
        ctx.restore();
        const grad = ctx.createRadialGradient(W / 2, H / 2, Math.max(W, H) * 0.2, W / 2, H / 2, Math.max(W, H) * 0.7);
        grad.addColorStop(0, "rgba(0,0,0,0)");
        grad.addColorStop(1, `rgba(0,0,0,${0.25 + 0.15 * breath})`);
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, W, H);
    } else if (style === "parallax") {
        const back = 1.08 + 0.02 * Math.sin(t * Math.PI * 2);
        const dxBack = Math.sin(t * Math.PI * 2) * 18;
        ctx.translate(W / 2 + dxBack, H / 2);
        ctx.scale(back, back);
        ctx.globalAlpha = 0.65;
        ctx.filter = "blur(8px)";
        ctx.drawImage(img, -W / 2, -H / 2, W, H);
        ctx.filter = "none";
        ctx.globalAlpha = 1;
        ctx.restore();
        ctx.save();
        const dxFront = -Math.sin(t * Math.PI * 2) * 8;
        ctx.translate(W / 2 + dxFront, H / 2);
        ctx.scale(1.0, 1.0);
        ctx.drawImage(img, -W / 2, -H / 2, W, H);
        ctx.restore();
    } else if (style === "kenburns") {
        const k = 1.06 + 0.06 * t; // slow zoom in
        const panX = -20 + 40 * t;
        const panY = 10 - 20 * t;
        ctx.translate(W / 2 + panX, H / 2 + panY);
        ctx.scale(k, k);
        ctx.drawImage(img, -W / 2, -H / 2, W, H);
        ctx.restore();
    } else if (style === "inkbloom") {
        ctx.translate(W / 2, H / 2);
        ctx.drawImage(img, -W / 2, -H / 2, W, H);
        ctx.restore();
        const r = Math.max(W, H);
        const innerR = r * (0.05 + 0.55 * t); // expanding clear circle
        const grad = ctx.createRadialGradient(W / 2, H / 2, innerR * 0.4, W / 2, H / 2, innerR);
        grad.addColorStop(0, "rgba(0,0,0,0)");
        grad.addColorStop(1, "rgba(0,0,0,0.85)");
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, W, H);
        // soft white bloom in center
        const bloom = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, r * 0.3);
        bloom.addColorStop(0, `rgba(255,235,210,${0.18 * (1 - Math.abs(0.5 - t) * 2)})`);
        bloom.addColorStop(1, "rgba(255,235,210,0)");
        ctx.fillStyle = bloom;
        ctx.fillRect(0, 0, W, H);
    } else if (style === "neon") {
        ctx.translate(W / 2, H / 2);
        const wob = 1 + 0.015 * Math.sin(t * Math.PI * 4);
        ctx.scale(wob, wob);
        ctx.drawImage(img, -W / 2, -H / 2, W, H);
        ctx.restore();
        const shimmer = 0.4 + 0.3 * Math.sin(t * Math.PI * 4);
        const top = ctx.createLinearGradient(0, 0, 0, H);
        top.addColorStop(0, `rgba(159,231,217,${0.16 * shimmer})`);
        top.addColorStop(0.5, "rgba(0,0,0,0)");
        top.addColorStop(1, `rgba(242,180,155,${0.16 * shimmer})`);
        ctx.fillStyle = top;
        ctx.fillRect(0, 0, W, H);
        // scanline shimmer band
        const bandY = (t * H * 1.4) - H * 0.2;
        const band = ctx.createLinearGradient(0, bandY - 80, 0, bandY + 80);
        band.addColorStop(0, "rgba(255,255,255,0)");
        band.addColorStop(0.5, "rgba(255,255,255,0.10)");
        band.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = band;
        ctx.fillRect(0, bandY - 80, W, 160);
    } else {
        ctx.translate(W / 2, H / 2);
        ctx.drawImage(img, -W / 2, -H / 2, W, H);
        ctx.restore();
    }
}

/**
 * Animated export — picks a style, records ~3-second loop via canvas + MediaRecorder.
 */
export default function AnimatedExport({ open, onOpenChange, card }) {
    const previewRef = useRef(null);
    const canvasRef = useRef(null);
    const sourceRef = useRef(null);
    const [recording, setRecording] = useState(false);
    const [supported, setSupported] = useState(true);
    const [progress, setProgress] = useState(0);
    const [style, setStyle] = useState("breath");

    useEffect(() => {
        if (!open) return;
        setProgress(0);
        setStyle(suggestStyle(card));
        if (typeof window === "undefined" || !("MediaRecorder" in window) || !("HTMLCanvasElement" in window)) {
            setSupported(false);
        }
    }, [open, card]);

    const recordWebm = async () => {
        if (!sourceRef.current || !canvasRef.current) return;
        setRecording(true);
        try {
            const node = sourceRef.current;
            const W = 540, H = 960;
            const canvas = canvasRef.current;
            canvas.width = W;
            canvas.height = H;
            const ctx = canvas.getContext("2d");

            const { toPng } = await import("html-to-image");
            const dataUrl = await toPng(node, { cacheBust: true, pixelRatio: 1, skipFonts: true });
            const img = new Image();
            img.crossOrigin = "anonymous";
            await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = dataUrl; });

            const stream = canvas.captureStream(FPS);
            const mime =
                MediaRecorder.isTypeSupported("video/webm;codecs=vp9") ? "video/webm;codecs=vp9" :
                MediaRecorder.isTypeSupported("video/webm;codecs=vp8") ? "video/webm;codecs=vp8" :
                MediaRecorder.isTypeSupported("video/webm") ? "video/webm" :
                "video/mp4";
            const chunks = [];
            const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 4_000_000 });
            recorder.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };

            const start = performance.now();
            let raf = 0;
            const draw = (t) => {
                const elapsed = t - start;
                const tt = Math.min(1, elapsed / DURATION_MS);
                setProgress(tt);
                drawFrame(style, ctx, img, W, H, tt);
                if (elapsed < DURATION_MS) raf = requestAnimationFrame(draw);
                else { cancelAnimationFrame(raf); recorder.stop(); }
            };
            recorder.start();
            raf = requestAnimationFrame(draw);
            await new Promise((res) => { recorder.onstop = res; });
            stream.getTracks().forEach((tr) => tr.stop());

            const ext = mime.includes("mp4") ? "mp4" : "webm";
            const blob = new Blob(chunks, { type: mime });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `moodcard-${card.style}-${style}.${ext}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success("Animated card saved", { description: `${style} · ${ext.toUpperCase()} · 3s loop` });
        } catch (e) {
            console.error("animate export failed", e);
            toast.error("Animate failed. Try again or use Download instead.");
        } finally {
            setRecording(false);
            setProgress(0);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        onClick={() => !recording && onOpenChange(false)}
                        className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm"
                    />
                    <motion.div
                        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
                        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
                        data-testid="animated-export-modal"
                    >
                        <div className="relative w-full max-w-sm rounded-3xl border border-app/30 bg-app-2 overflow-hidden pointer-events-auto">
                            <div className="absolute inset-0 grain pointer-events-none" />
                            <div className="relative p-5">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <Sparkles className="w-4 h-4 text-app" strokeWidth={1.5} />
                                        <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-app">animate</span>
                                    </div>
                                    <button
                                        onClick={() => !recording && onOpenChange(false)}
                                        disabled={recording}
                                        data-testid="animated-export-close"
                                        className="p-2 rounded-full text-app-2 hover:text-app hover:bg-surface-2 transition-colors disabled:opacity-30"
                                    >
                                        <X className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                </div>

                                {/* Preview (Framer animated) — varies subtly by style */}
                                <motion.div
                                    ref={previewRef}
                                    animate={
                                        style === "parallax" ? { x: [0, 14, -10, 0] } :
                                        style === "kenburns" ? { scale: [1, 1.08], x: [0, 8], y: [0, -6] } :
                                        style === "inkbloom" ? { opacity: [1, 0.85, 1] } :
                                        style === "neon"     ? { scale: [1, 1.015, 1] } :
                                        { scale: [1, 1.03, 1], x: [0, 6, -4, 0], y: [0, -4, 4, 0] }
                                    }
                                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                    className="origin-center"
                                >
                                    <MoodCard card={card} moodId={card._moodId || null} />
                                </motion.div>

                                {/* Style picker */}
                                <div className="mt-4 -mx-1 flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="animate-style-picker">
                                    {ANIM_STYLES.map((s) => {
                                        const active = s.key === style;
                                        return (
                                            <button
                                                key={s.key}
                                                onClick={() => !recording && setStyle(s.key)}
                                                data-testid={`animate-style-${s.key}`}
                                                className={`shrink-0 px-3 py-1.5 rounded-full border transition-colors ${
                                                    active
                                                        ? "border-app bg-cta text-app-inverse"
                                                        : "border-app/30 bg-surface-2 text-app-2 hover:text-app hover:border-app/60"
                                                }`}
                                            >
                                                <span className="font-sans text-[11px] uppercase tracking-[0.18em]">{s.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Hidden source — same card with stable rendering for capture */}
                                <div style={{ position: "fixed", left: -99999, top: 0, pointerEvents: "none" }}>
                                    <div ref={sourceRef} style={{ width: 540, height: 960 }}>
                                        <MoodCard card={card} moodId={card._moodId || null} size="full" />
                                    </div>
                                    <canvas ref={canvasRef} />
                                </div>

                                <div className="mt-4">
                                    {!supported ? (
                                        <p className="font-sans text-xs text-app-3 text-center py-3">
                                            Your browser can't record yet. Use a recent Chrome / Firefox.
                                        </p>
                                    ) : recording ? (
                                        <div className="space-y-3">
                                            <div className="relative h-1 rounded-full bg-surface-2 overflow-hidden">
                                                <div
                                                    className="absolute inset-y-0 left-0 transition-[width]"
                                                    style={{ width: `${progress * 100}%`, backgroundColor: "var(--app-text)" }}
                                                />
                                            </div>
                                            <div className="flex items-center justify-center gap-2 text-app-2 font-sans text-xs">
                                                <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
                                                <span>Recording 3s loop…</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={recordWebm}
                                            data-testid="animated-export-record"
                                            className="w-full py-3.5 rounded-full bg-cta text-app-inverse font-sans text-sm font-medium tracking-wide hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                                        >
                                            <Download className="w-4 h-4" strokeWidth={1.6} />
                                            Export animated card
                                        </button>
                                    )}
                                </div>
                                <p className="mt-3 text-center font-sans text-[11px] text-app-3">
                                    3s · {ANIM_STYLES.find(s => s.key === style)?.hint || "soft loop"} · loops on IG / TikTok
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
